import { useEffect, useRef, useState } from 'react'
import type { ChartGridLayout } from '../TickerPriceChart'

const CHART_LAYOUTS: { id: ChartGridLayout; label: string }[] = [
  { id: 'single', label: 'Single' },
  { id: '1x2', label: '2 across' },
  { id: '2x1', label: '2 stacked' },
  { id: '2x2', label: '2 × 2 grid' },
  { id: '2x3', label: '2 × 3 grid' },
  { id: '3x3', label: '3 × 3 grid' },
]

const DEFAULT_LAYOUT_OPTIONS: ChartGridLayout[] = ['single', '1x2', '2x1', '2x2']

export function LayoutGlyph({ layout }: { layout: ChartGridLayout }) {
  if (layout === '1x2') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <rect x="1.5" y="2.5" width="4.6" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="7.9" y="2.5" width="4.6" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    )
  }
  if (layout === '2x1') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <rect x="2.5" y="1.5" width="9" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="2.5" y="7.9" width="9" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    )
  }
  if (layout === '2x2') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <rect x="1.5" y="1.5" width="4.6" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="7.9" y="1.5" width="4.6" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="1.5" y="7.9" width="4.6" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="7.9" y="7.9" width="4.6" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    )
  }
  if (layout === '2x3' || layout === '3x3') {
    const rows = layout === '3x3' ? [1.5, 5.4, 9.3] : [2.5, 8]
    const cols = [1.5, 5.4, 9.3]
    const h = layout === '3x3' ? 3.2 : 3.5
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        {rows.flatMap((y) =>
          cols.map((x) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="3.2" height={h} rx="0.8" stroke="currentColor" strokeWidth="1.1" />
          )),
        )}
      </svg>
    )
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="1.5" y="2.5" width="11" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}

/**
 * Page-level chart grid layout picker. The layout is a property of the page (how
 * many chart cells to show), not of any individual chart — so this lives in the
 * page header, once, rather than on every chart.
 */
export function ChartLayoutPicker({
  layout,
  onLayoutChange,
  layoutOptions,
}: {
  layout: ChartGridLayout
  onLayoutChange: (layout: ChartGridLayout) => void
  layoutOptions?: ChartGridLayout[]
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Chart layout"
        aria-label="Chart layout"
        className={`px-1.5 py-1 rounded border transition-colors ${
          layout !== 'single'
            ? 'bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25'
            : 'border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700'
        }`}
      >
        <LayoutGlyph layout={layout} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1 z-30 min-w-[110px] bg-zinc-900 border border-zinc-700 rounded shadow-xl py-1"
        >
          {CHART_LAYOUTS.filter((l) =>
            (layoutOptions ?? DEFAULT_LAYOUT_OPTIONS).includes(l.id),
          ).map((l) => (
            <button
              key={l.id}
              type="button"
              role="option"
              aria-selected={l.id === layout}
              onClick={() => {
                onLayoutChange(l.id)
                setOpen(false)
              }}
              className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors flex items-center gap-2 ${
                l.id === layout
                  ? 'bg-sky-500/15 text-sky-300'
                  : 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100'
              }`}
            >
              <LayoutGlyph layout={l.id} />
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
